import { useCallback, useRef } from "react";
import {
  SegmentProduct,
  SEGMENT_LIST_FORMAT,
  SEGMENT_LIST_ID,
  SEGMENT_LIST_TYPE,
} from "src/segment";
import {
  CatalogProduct,
  HomepageSingleCategoryProduct,
} from "src/types/graphql.d";
import { useDebouncedCallback } from "use-debounce";
import useProductListViewed from "./useProductListViewed";
import { ContextKey } from "src/utils/product-list-context-keys";
import { useFeatureFlags } from "./useFeatureFlags";
import { useIsLoggedIn } from "./useIsLoggedIn";
import { trackProductListViewedEvent } from "src/tracking/events/product-list-viewed";
import { trackProductClickedEvent } from "src/tracking/events/product-list-item-selected";
import { trackProductListClickedEvent } from "src/tracking/events/product-list-clicked";

export enum LimitType {
  DURATION = 1,
  LENGTH,
}

type SegmentProductListProps = {
  defaultSortBy?: string;
  listFormat: SEGMENT_LIST_FORMAT;
  listId: SEGMENT_LIST_ID | string;
  listType: SEGMENT_LIST_TYPE | ContextKey;
  numberOfItems: number;
  shopType?: string;
  responseSourceType?: string | null;
  query?: string;
  resultId?: string;
};

export type SegmentProductList = {
  segmentProductOnClick: (
    product: CatalogProduct | HomepageSingleCategoryProduct,
    position: number
  ) => void;
  segmentViewAllOnClick: () => void;
  setVisibleProducts: (
    product: CatalogProduct | HomepageSingleCategoryProduct,
    position: number,
    limitType?: LimitType
  ) => void;
};

type Ref = {
  products: SegmentProduct[];
};

function useSegmentProductList({
  defaultSortBy,
  listFormat,
  listId,
  listType,
  numberOfItems,
  shopType,
  responseSourceType,
  query,
  resultId,
}: SegmentProductListProps): SegmentProductList {
  const ref = useRef<Ref>({
    products: [],
  });

  const isLoggedIn = useIsLoggedIn();
  const { enableGatedHomepage } = useFeatureFlags();

  const { addProductToQueue } = useProductListViewed({
    resultId,
    defaultSortBy,
    listFormat,
    listId,
    listType,
    numberOfItems,
    shopType,
    query,
  });

  const debounced = useDebouncedCallback((products: SegmentProduct[]) => {
    if (products?.length) {
      void trackProductListViewedEvent({
        resultId,
        defaultSortBy,
        productListMeta: {
          listFormat,
          listId,
          listType,
          shopType,
        },
        numberOfItems,
        products,
        responseSourceType,
        query,
        gated: !!(enableGatedHomepage && !isLoggedIn),
      });
      ref.current.products = [];
    }
  }, 500);

  const setVisibleProducts = useCallback(
    (
      product: CatalogProduct | HomepageSingleCategoryProduct,
      position: number,
      limitType: LimitType = LimitType.DURATION
    ) => {
      if (limitType === LimitType.DURATION) {
        ref.current.products = [
          ...ref.current.products,
          { ...product, position: position + 1 },
        ];

        debounced(ref.current.products);
      } else {
        addProductToQueue({
          ...product,
          position,
        });
      }
    },
    [addProductToQueue, debounced]
  );

  const segmentViewAllOnClick = useCallback(() => {
    void trackProductListClickedEvent({
      listFormat,
      listId,
      listType,
      shopType,
      resultId,
    });
  }, [listFormat, listId, listType, shopType, resultId]);

  const segmentProductOnClick = useCallback(
    (
      product: CatalogProduct | HomepageSingleCategoryProduct,
      position: number
    ) =>
      void trackProductClickedEvent({
        position,
        product,
        productListMeta: {
          listFormat,
          listId,
          listType,
          shopType,
        },
        query,
        resultId,
      }),
    [listFormat, listId, listType, shopType, query, resultId]
  );

  return {
    segmentProductOnClick,
    segmentViewAllOnClick,
    setVisibleProducts,
  };
}

export { useSegmentProductList };
